/**
 * @file edit-profile-modal.tsx
 * @description
 * -----------------------------------------------------
 * Archivo que contiene el modal para editar el perfil de usuario
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2026-04-07
 * @author DLTCode
 * @author Sergio Manjón
 * @copyright © DLTCode
 * @license Private
 * */
"use client";

import { toast } from "sonner";
import { useEffect, useState, useTransition } from "react";
import { useTranslations } from "next-intl";
import ModalComponent from "@/components/modals/ModalComponent";
import InputComponent from "@/components/ui/InputComponent";
import { useSession } from "@/features/auth";
import { EditProfileSchemaType } from "@/schemas/UserSchema";
import ToggleComponent from "@/components/ui/ToggleComponent";
import { editProfile, useProfileView } from "@/features/profile";
import { PROFILE_EDIT_MODAL_ID } from "./profile-modal-ids";
export const EditProfileModal = () => {
  const modals = useTranslations("Modals.editProfile");
  const labels = useTranslations("Labels");
  const placeholders = useTranslations("Placeholders");
  const responseMessagesT = useTranslations("ResponseMessages");
  const {
    update
  } = useSession();
  const {
    closeEditProfileModal,
    isEditProfileModalOpen,
    user
  } = useProfileView();
  const [formData, setFormData] = useState<EditProfileSchemaType>({
    name: user?.name || "",
    twoFactorCode: user?.twoFactorCode || false
  });
  const [errors, setErrors] = useState<{
    [key: string]: string[];
  }>({});
  const [isPending, startTransition] = useTransition();

  // Reinicia los valores del formulario al abrir el modal
  useEffect(() => {
    if (isEditProfileModalOpen) {
      setFormData({
        name: user?.name || "",
        twoFactorCode: user?.twoFactorCode || false
      });
      setErrors({});
    }
  }, [isEditProfileModalOpen, user?.name, user?.twoFactorCode]);

  // Verifica si hay cambios en el formulario
  const hasChanges = formData.name !== (user?.name || "") || formData.twoFactorCode !== (user?.twoFactorCode || false);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const {
      id,
      value
    } = event.target;
    setFormData(prevFormData => ({
      ...prevFormData,
      [id]: value
    }));
    if (errors[id]) {
      setErrors(prevErrors => {
        const newErrors = {
          ...prevErrors
        };
        delete newErrors[id];
        return newErrors;
      });
    }
  };
  const handleSubmit = async (submittedData: FormData) => {
    startTransition(async () => {
      try {
        const response = await editProfile(submittedData);
        if (response && "fieldErrors" in response) {
          setErrors(response.fieldErrors);
          return;
        }
        if (response && "error" in response && response.message) {
          toast.error(response.message);
          return;
        }
        await update({
          name: submittedData.get("name") as string,
          twoFactorCode: formData.twoFactorCode
        });
        closeEditProfileModal();
        toast.success(response?.message || responseMessagesT("UPDATE_PROFILE"));
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        }
      }
    });
  };
  return <ModalComponent<EditProfileSchemaType> id={PROFILE_EDIT_MODAL_ID} title={modals("title")} isOpen={isEditProfileModalOpen} onClose={closeEditProfileModal} formAction={handleSubmit} loadingConfirm={isPending} buttonSubmitText="buttonSaveChanges" buttonCancel={true} formData={formData} hideSubmitIcon disabledConfirm={!hasChanges}>
      <InputComponent id="mail" name="mail" label={labels("labelMail")} className="input--full" staticDisplay={true} value={user.mail} />
      <InputComponent id="name" name="name" label={labels("labelName")} type="text" placeholder={placeholders("placeholderName")} className="input--full" autoComplete="name" readonly={false} value={formData.name} onChange={handleChange} error={errors.name && errors.name[0]} isRequired />
      <section className="input__group">
        {/*
          Etiqueta visible para el toggle. El nombre accesible del control se define
          vía `aria-label` en el ToggleComponent como "Activar o desactivar doble factor de autenticación"
          (WCAG 1.3.1 / 4.1.2). Se elimina la prop `label` interna del toggle
          para que no se anuncie un nombre redundante ("Activado"/"Desactivado").
         */}
        <span className="label">
          {labels("label2FA")}
        </span>
        <ToggleComponent id="twoFactorCode" name="twoFactorCode" checked={formData.twoFactorCode ?? false} aria-label={labels("toggle2FA")} onChange={() => {
        setFormData(prev => ({
          ...prev,
          twoFactorCode: !prev.twoFactorCode
        }));
      }} />
      </section>
    </ModalComponent>;
};